import { type AnalyticsInstance } from 'analytics'
import { getCookie } from 'cookies-next'
import { EVERFLOW_TRANSACTION_ID_COOKIE } from '@/constants/cookies'
import { getStringFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage/local-storage'
import { EVERFLOW_TRANSACTION_ID } from '@/utils/local-storage/local-storage-keys'
import { namedLogger } from '../utils'
import { type AnalyticsPlugin } from './types'

const EF_TRANSACTION_ID_QUERY_PARAM = '_ef_transaction_id' as const

const logger = namedLogger('everflow Analytics Plugin')

type AnalyticsEventParams = {
  payload: {
    properties: {
      _ef_transaction_id?: string
    }
  }
}

function getEverflowTransactionId(analyticsInstance?: AnalyticsInstance) {
  logger.debug('Everflow getEverflowTransactionId')
  if (typeof window === 'undefined') return
  try {
    const { searchParams } = new URL(window.location.href, window.location.origin)
    const paramsEfTransactionId = searchParams.get(EF_TRANSACTION_ID_QUERY_PARAM)
    if (paramsEfTransactionId) {
      writeToLocalStorage(EVERFLOW_TRANSACTION_ID, paramsEfTransactionId)
      return paramsEfTransactionId
    }

    const cookieEfTransactionId = getCookie(EVERFLOW_TRANSACTION_ID_COOKIE)
    if (typeof cookieEfTransactionId === 'string' && cookieEfTransactionId) {
      writeToLocalStorage(EVERFLOW_TRANSACTION_ID, cookieEfTransactionId)
      return cookieEfTransactionId
    }

    const localStorageEfTransactionId = getStringFromLocalStorage(EVERFLOW_TRANSACTION_ID)
    if (localStorageEfTransactionId) {
      return localStorageEfTransactionId
    }

    return analyticsInstance?.user()?.traits?._ef_transaction_id || undefined
  } catch (error) {
    return
  }
}

function attachEverflow(params: AnalyticsEventParams, analyticsInstance?: AnalyticsInstance) {
  let _ef_transaction_id = params.payload.properties._ef_transaction_id

  if (!_ef_transaction_id) {
    _ef_transaction_id = getEverflowTransactionId(analyticsInstance)
  }

  logger.debug('_ef_transaction_id', { _ef_transaction_id })
  if (_ef_transaction_id) {
    params.payload.properties._ef_transaction_id = _ef_transaction_id
  }
}

export function everflowAnalyticsPlugin() {
  let analyticsInstance: AnalyticsInstance | undefined

  const plugin: AnalyticsPlugin = {
    name: 'everflow',

    initialize({ instance }) {
      analyticsInstance = instance
    },

    page() {
      try {
        const _ef_transaction_id = getEverflowTransactionId(analyticsInstance)
        logger.debug('page', { _ef_transaction_id })
        if (_ef_transaction_id && _ef_transaction_id !== analyticsInstance?.user()?.traits?._ef_transaction_id) {
          analyticsInstance?.identify(analyticsInstance?.user()?.userId, { _ef_transaction_id })
        }
      } catch (error) {
        logger.error('page error', undefined, error)
      }
    },

    pageStart(params: AnalyticsEventParams) {
      try {
        logger.debug('pageStart', params)
        attachEverflow(params, analyticsInstance)
      } catch (error) {
        logger.error('pageStart error', undefined, error)
      }
    },

    trackStart(params: AnalyticsEventParams) {
      try {
        logger.debug('trackStart', params)
        attachEverflow(params, analyticsInstance)
      } catch (error) {
        logger.error('trackStart error', undefined, error)
      }
    },

    loaded() {
      logger.debug('loaded')
      return true
    },
  }

  return plugin
}
